<template>
  <div class="sli-form-page">
    <section class="sli-sheet sli-sheet--title">
      <div class="sli-list-head d-flex align-items-center justify-content-between">
        <div>
          <h1 class="mb-0">
            قائمة حصر وتحديد — أراضي الاستيلاء
          </h1>
          <p>قرار التخصيص حصر، والقطع بداخله، وكل تعدٍ يمكن أن يصبح مشروعاً مستقلاً.</p>
        </div>
        <b-button
          v-if="isInventoryTab && (hasPermission('create seizure_land_inventories') || hasPermission('create projects'))"
          variant="primary"
          class="rounded-pill"
          :to="createTo"
        >
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
          إضافة حصر جديد
        </b-button>
      </div>
    </section>

    <sli-module-tabs
      :tabs="tabs"
      :value="activeTab"
      @input="setTab"
    />

    <sli-maps-panel v-if="isMapsTab" />

    <template v-else>
      <b-card class="p-0 filter-card rounded-lg">
        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="بحث"
              label-for="sli-search"
            >
              <b-form-input
                id="sli-search"
                v-model="filter.search"
                placeholder="رقم الشهر، كود الأرض، اسم الخاضع، القطعة"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
          >
            <b-form-group
              label="محافظة"
              label-for="sli-filter-gov"
            >
              <v-select
                id="sli-filter-gov"
                v-model="filter.governorate"
                :options="governorateOptions"
                :get-option-label="govLabel"
                :reduce="val => govLabel(val)"
                :dir="'rtl'"
                :clearable="true"
                placeholder="المحافظة"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <div
        v-if="loading"
        class="text-center py-2"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="!rows.length"
        class="sli-sheet"
        style="padding: 18px;"
      >
        لا توجد بيانات
      </div>
      <div
        v-else
        class="sli-inv-grid"
      >
        <sli-inventory-card
          v-for="(item, index) in rows"
          :key="item.id || index"
          :item="item"
          :index="index"
          :tab="activeTab"
          @link="openLink"
        />
      </div>
      <b-pagination
        v-if="meta && meta.total"
        v-model="filter.page"
        align="center"
        :total-rows="meta.total"
        :per-page="meta.per_page || filter.per_page"
        @change="onPageChange"
      />
    </template>

    <sli-link-seizure-modal
      :visible.sync="linkModal.visible"
      :inventory-id="linkModal.id"
      @linked="fetchList"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput,
  BButton, BSpinner, BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import governoratesApi from '@/api/settings/governorates'
import seizureLandInventoriesApi from '@/api/seizure-land-inventories/seizureLandInventories'
import SliModuleTabs from './components/SliModuleTabs.vue'
import SliInventoryCard from './components/SliInventoryCard.vue'
import SliMapsPanel from './components/SliMapsPanel.vue'
import SliLinkSeizureModal from './components/SliLinkSeizureModal.vue'
import {
  unwrapList,
  unwrapTabs,
  optionLabel,
  HEADER_AGRICULTURAL_REFORM,
  TAB_MAPS,
  DEFAULT_MODULE_TABS,
} from './formModel'

export default {
  name: 'SeizureLandInventoriesList',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    BPagination,
    vSelect,
    SliModuleTabs,
    SliInventoryCard,
    SliMapsPanel,
    SliLinkSeizureModal,
  },
  data() {
    return {
      loading: false,
      rows: [],
      meta: {},
      tabs: DEFAULT_MODULE_TABS.slice(),
      governorateOptions: [],
      searchTimer: null,
      filter: {
        search: '',
        governorate: null,
        page: 1,
        per_page: 15,
      },
      linkModal: {
        visible: false,
        id: null,
      },
    }
  },
  computed: {
    activeTab() {
      return this.$route.query.tab || HEADER_AGRICULTURAL_REFORM
    },
    isMapsTab() {
      return this.activeTab === TAB_MAPS
    },
    isInventoryTab() {
      return !this.isMapsTab
    },
    createTo() {
      return {
        name: 'seizure-land-inventories-create',
        query: { tab: this.activeTab, header_type: this.activeTab },
      }
    },
  },
  watch: {
    'filter.search'() { this.debounceFetch() },
    'filter.governorate'() { this.fetchList(1) },
    activeTab() {
      if (this.isInventoryTab) this.fetchList(1)
    },
  },
  mounted() {
    if (!this.$route.query.tab) {
      this.$router.replace({ query: { ...this.$route.query, tab: HEADER_AGRICULTURAL_REFORM } })
    }
    this.loadTabs()
    this.loadGovernorates()
    if (this.isInventoryTab) this.fetchList()
  },
  beforeDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    govLabel(option) {
      return optionLabel(option)
    },
    setTab(key) {
      if (key === this.activeTab) return
      this.$router.replace({ query: { ...this.$route.query, tab: key } })
    },
    loadTabs() {
      seizureLandInventoriesApi.moduleTabs()
        .then(res => {
          this.tabs = unwrapTabs(res)
        })
        .catch(() => {
          this.tabs = DEFAULT_MODULE_TABS.slice()
        })
    },
    loadGovernorates() {
      governoratesApi.dropdown()
        .then(res => {
          const payload = Array.isArray(res) ? res : (res?.data || [])
          this.governorateOptions = Array.isArray(payload) ? payload : []
        })
        .catch(() => {
          this.governorateOptions = []
        })
    },
    debounceFetch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => this.fetchList(1), 400)
    },
    cleanQuery() {
      const q = {
        page: this.filter.page,
        per_page: this.filter.per_page,
        header_type: this.activeTab,
      }
      if (this.filter.search) q.search = this.filter.search
      if (this.filter.governorate) q.governorate = this.filter.governorate
      return q
    },
    fetchList(page) {
      if (this.isMapsTab) return
      if (page) this.filter.page = page
      this.loading = true
      seizureLandInventoriesApi.list(this.cleanQuery())
        .then(res => {
          const { rows, meta } = unwrapList(res)
          this.rows = rows
          this.meta = meta || {}
        })
        .catch(() => {
          this.rows = []
          this.$toast.error('تعذر تحميل القائمة')
        })
        .finally(() => {
          this.loading = false
        })
    },
    onPageChange(page) {
      this.fetchList(page)
    },
    openLink(item) {
      this.linkModal = { visible: true, id: item.id }
    },
  },
}
</script>

<style>
.sli-form-page {
  background: #e8eef5;
  margin: -1.2rem -1.2rem 0;
  padding: 16px 12px 96px;
  min-height: calc(100vh - 80px);
  overflow-x: hidden;
}
.sli-inv-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  margin-bottom: 16px;
  align-items: stretch;
}
.sli-inv-card {
  margin-bottom: 0;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  height: 100%;
}
.sli-inv-card__body {
  padding: 14px 16px 16px;
}
.sli-inv-card__title {
  margin: 0 0 6px;
  color: #06284D;
  font-size: 1.05rem;
  font-weight: 700;
}
.sli-inv-card__meta {
  margin: 0 0 4px;
  color: #06284D;
  font-size: 0.9rem;
}
.sli-inv-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 10px 0;
}
.sli-chip {
  display: inline-block;
  background: #fff;
  border: 1px solid rgba(6, 40, 77, 0.18);
  color: #06284D;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 0.78rem;
  font-weight: 600;
}
.sli-chip--on {
  background: #eef6e4;
  border-color: #5CC10B;
}
.sli-inv-card__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
}
.sli-text-link {
  color: #06284D;
  font-weight: 700;
  font-size: 0.85rem;
}
.sli-text-link:hover {
  color: #5CC10B;
}
.sli-save--sm {
  min-width: auto;
  padding: 6px 14px;
  font-size: 0.85rem;
  height: auto;
}
.sli-list-head {
  gap: 12px;
}
.sli-sheet {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(6, 40, 77, 0.12), 0 1px 3px 1px rgba(6, 40, 77, 0.08);
  margin-bottom: 12px;
  overflow: visible;
}
.sli-sheet--title {
  background: #06284D;
  border-top: 8px solid #5CC10B;
  padding: 16px 18px 14px;
  overflow: hidden;
}
.sli-sheet--title h1 {
  font-size: 1.35rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
}
.sli-sheet--title p {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.9rem;
}
.sli-sheet--lands {
  background: #f4f7fb;
  border-top: 6px solid #06284D;
}
.sli-sheet__head {
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #06284D;
  border-bottom: 1px solid rgba(6, 40, 77, 0.1);
}
.sli-location {
  border-top: 8px solid #e8eef5;
}
.sli-location.is-navy {
  background: #e8eef5;
  border-right: 4px solid #06284D;
}
.sli-location.is-lime {
  background: #eef6e4;
  border-right: 4px solid #5CC10B;
}
.sli-grid.sli-grid--fit {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0;
}
.sli-cell {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  box-sizing: border-box;
  width: max-content;
  max-width: 100%;
  min-width: 0;
  padding: 8px 12px 10px 14px;
  border-top: 1px solid rgba(6, 40, 77, 0.1);
  border-left: 1px solid rgba(6, 40, 77, 0.1);
  margin: 0;
}
.sli-cell--fit {
  width: calc(var(--sli-fit-ch, 12) * 1ch + 32px);
}
.sli-cell__label {
  display: block;
  font-size: 11px;
  line-height: 1.25;
  color: #06284D;
  margin: 0 0 4px;
  font-weight: 600;
  white-space: nowrap;
}
.sli-cell__input {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  border: 0;
  border-bottom: 1px solid #c5ced8;
  border-radius: 0;
  background: transparent;
  height: 32px;
  padding: 2px 0;
  font-size: 0.95rem;
  color: #06284D;
}
.sli-cell__err {
  color: #d93025;
  font-size: 0.8rem;
}
.sli-add-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px 12px;
}
.sli-add-btn {
  background: transparent;
  border: 0;
  color: #06284D;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 4px 0;
  cursor: pointer;
}
.sli-link-btn {
  background: none;
  border: 0;
  color: #d93025;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
}
.sli-radios {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-height: 32px;
  font-size: 0.85rem;
}
.sli-save {
  background: #06284D;
  color: #fff;
  border: 0;
  border-radius: 4px;
  min-width: 120px;
  height: 40px;
  font-weight: 600;
  cursor: pointer;
}
.sli-save:hover:not(:disabled) {
  background: #5CC10B;
}
@media (max-width: 1199.98px) {
  .sli-inv-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 991.98px) {
  .sli-list-head {
    flex-wrap: wrap;
    align-items: flex-start !important;
  }
  .sli-list-head h1,
  .sli-list-head h3 {
    font-size: 1.15rem;
    flex: 1 1 auto;
  }
  .sli-list-head .btn {
    min-height: 44px;
  }
}
</style>
